import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { zonePropertiesType } from '../../types';
import ExpandedListTab from '../ExpandedListTab';
import SaveButton from '../buttons/SaveButton';
import useExtendedState from '../../hooks/useExtendedState';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_ZONES_QUERY_NAME, CREATE_ZONE } from '../../graphql/zones';
import TextField from '../fields/TextField';
import {
    codeValidator,
    nameValidator,
    ZONE_CODE_MAX_LETTERS,
    ZONE_NAME_MAX_LETTERS,
} from '../../validators/zones';
import useMultiValidator from '../../hooks/useMultiValidator';
import SelectField from '../fields/SelectField';
import FlagIconField from '../fields/FlagIconField';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

ZoneCreateTab.propTypes = {
    hideHandler: PropTypes.func.isRequired,
    zoneProps: zonePropertiesType.isRequired,
};

function ZoneCreateTab(props) {
    const {
        hideHandler,
        zoneProps,
    } = props;
    const {
        availableClearingHouses,
        availableCurrencies,
    } = zoneProps;
    const { t } = useTranslation();
    const nameState = useExtendedState('');
    const codeState = useExtendedState('');
    const flagCodeState = useExtendedState('');
    const clearingHouseCodeState = useExtendedState(availableClearingHouses[0].code);
    const currencyCodeState = useExtendedState(availableCurrencies[0].code);
    const { validators, isValid } = useMultiValidator(false, {
        name: compositeValidator(nameValidator(t), wafValidator(t)),
        code: compositeValidator(codeValidator(t), wafValidator(t)),
    });
    const [isDisabled, setIsDisabled] = useState(true);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const extendedMutation = useExtendedMutation(CREATE_ZONE, {
        variables: {
            name: nameState.value,
            code: codeState.value,
            flagCode: flagCodeState.value,
            clearingHouseCode: clearingHouseCodeState.value,
            currencyCode: currencyCodeState.value,
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'zones.notification.zone-create-success', dataProperty: 'createZone' },
        failureMessageOptions: { key: 'zones.notification.zone-create-failure', params: { name: nameState.value, code: codeState.value } },
        refetchQueries: [ALL_ZONES_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="zone-create-tab"
            title={t('zones.zone-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <TextField
                    label={t('zones.name')}
                    state={nameState}
                    validator={validators.name}
                    maxLength={ZONE_NAME_MAX_LETTERS}
                    className="zone-create-tab--fields--name wide"
                    placeholder={t('zones.placeholder.name')}
                    isRequired
                />
                <TextField
                    label={t('zones.code')}
                    state={codeState}
                    validator={validators.code}
                    maxLength={ZONE_CODE_MAX_LETTERS}
                    className="zone-create-tab--fields--code"
                    placeholder={t('zones.placeholder.code')}
                    isRequired
                />
                <FlagIconField
                    label={t('zones.flag')}
                    state={flagCodeState}
                    inputId="zone-create-tab--fields--flag"
                    className="zone-create-tab--fields--flag"
                />
                <SelectField
                    label={t('zones.clearing-house')}
                    state={clearingHouseCodeState}
                    options={availableClearingHouses.map((availableClearingHouse) => (
                        <option value={availableClearingHouse.code} key={availableClearingHouse.code}>{availableClearingHouse.name} ({availableClearingHouse.code})</option>
                    ))}
                    className="zone-create-tab--fields--clearing-house"
                    inputId="zone-create-tab--fields--clearing-house"
                    isRequired
                />
                <SelectField
                    label={t('zones.currency')}
                    state={currencyCodeState}
                    options={availableCurrencies.map((availableCurrency) => (
                        <option value={availableCurrency.code} key={availableCurrency.code}>{availableCurrency.name} ({availableCurrency.code})</option>
                    ))}
                    className="zone-create-tab--fields--currency"
                    inputId="zone-create-tab--fields--currency"
                    isRequired
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    confirmationMessage={t('zones.confirm-create-zone', { name: nameState.value, code: codeState.value })}
                />
            </div>
        </ExpandedListTab>
    );
}

export default ZoneCreateTab;
